/**
 * The duel. Two people, one brief from the curriculum, one deadline, and a blind vote that says
 * what decided it.
 *
 * Pure functions over (duel, tally, now) — no I/O, no clock read, no randomness. The browser and
 * the Worker both run this file, so what the battle screen draws and what the database enforces
 * cannot drift apart. Every state is a nullable timestamp on one row, which is why there is no
 * status column: a transition is `UPDATE … WHERE <column> IS NULL`, and running it twice does
 * nothing.
 *
 * **Nothing in here counts anything about a person.** No wins, no losses, no record, no ranking.
 * A duel is an event that happened on a Tuesday, like a posted work. The reasoning is in
 * `docs/DVOBOJ.md` §2, and `tests/dvoboj.test.ts` reads this source and fails if a per-member
 * aggregate ever appears in it — the same guard `posada.ts` already carries.
 *
 * The two numbers this product has are unchanged. Submitting to a duel posts a `rad`; the
 * revision afterwards adds a `popravljen`. The duel adds neither a third number nor a ladder.
 */

import type { ResolvedContent, ResolvedNode } from './content';
import { isNodeUnlocked, type PlayerState } from './rules';

/* ------------------------------------------------------------------ vocabulary */

/**
 * The five things a vote can say decided it. Five, and it stays five.
 *
 * They are the vocabulary the courses already teach, they cover an edit without overlapping, and
 * a forced choice among five is what turns a vote into a judgement somebody can act on. This is
 * the design's whole answer to GDD §10.1: a vote here cannot express „this person is better",
 * only „this thing beat that thing, on rhythm".
 */
export const OSE = ['ritam', 'rez', 'zvuk', 'boja', 'ideja'] as const;
export type Osa = (typeof OSE)[number];

/** Side 0 is the challenger, side 1 the challenged. Everything in this file uses that order. */
export type Side = 0 | 1;
export const SIDES: readonly Side[] = [0, 1];
export const other = (side: Side): Side => (side === 0 ? 1 : 0);

/** How long a member has to submit. Fixed options — this product does not hand out date pickers. */
export const ROKOVI_RADA = [24, 48, 72, 168] as const;
/** How long the community then has to decide. */
export const ROKOVI_GLASANJA = [24, 48, 72] as const;

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** An unanswered challenge stops being an offer and starts being a debt. */
export const POZIV_TTL_MS = 72 * HOUR;
/** Sent challenges awaiting an answer, per member. */
export const MAX_OTVORENIH_POZIVA = 3;
/** After a decline, the same person cannot come back for a week. */
export const PAUZA_POSLE_ODBIJANJA_MS = 7 * DAY;
/** Proposals exchanged before the two of them should be talking on Skool instead. */
export const MAX_RUNDI = 4;

/**
 * Below this, „the community decided" is not true. A verdict from one person is the worst possible
 * artefact of this feature, so it is not produced at all.
 */
export const MIN_GLASOVA = 3;

/**
 * Votes at which the health bars become a straight share of the vote. Below it the bars still move
 * on every vote, so a small duel is not a pair of full bars that jump at the end.
 */
export const HP_SKALA = 12;
/** Nobody sits at zero for two days. The floor lifts only when the duel resolves. */
export const HP_POD = 5;

/* ------------------------------------------------------------------ the row */

/**
 * One duel, exactly as it is stored. Timestamps only: each one is a step in the round trip, and
 * `COALESCE(col, ?)` makes every write once-only.
 *
 * There is deliberately no `winner_id` cache here. The outcome is derived from the votes by
 * `ishodOf`, so the screen and the database cannot disagree about who won — and a column nobody
 * writes is a column nobody can write wrongly.
 */
export interface Duel {
  id: string;
  /** The challenger. Side 0. */
  fromId: string;
  fromName: string;
  /** The challenged. Side 1. */
  toId: string;
  toName: string;

  /** The brief: a curriculum node that asks for a link. */
  nodeId: string;
  workHours: number;
  voteHours: number;

  /** When the terms now on the table were proposed. Reset by every counter-offer. */
  termsAt: number;
  /** Proposals exchanged so far. 0 means the original challenge is still standing. */
  rounds: number;
  /** Who proposed the terms now on the table. Null on the original challenge. */
  counterBy: string | null;

  acceptedAt: number | null;
  declinedAt: number | null;
  cancelledAt: number | null;
  cancelledBy: string | null;

  fromUrl: string | null;
  fromAt: number | null;
  toUrl: string | null;
  toAt: number | null;

  /** Stamped when the outcome was first written down — see `ishodOf` for why it is not the truth. */
  resolvedAt: number | null;
  fromRevisedAt: number | null;
  toRevisedAt: number | null;
  /** The challenger has been shown the answer to their challenge. Stops it announcing twice. */
  seenAt: number | null;
}

/** Votes, by aggregate only. There is no shape in this file that can hold a voter's identity. */
export interface Tally {
  /** Total votes for [side 0, side 1]. */
  votes: [number, number];
  /** Per axis, votes for [side 0, side 1]. */
  axes: Record<Osa, [number, number]>;
}

export const emptyTally = (): Tally => ({
  votes: [0, 0],
  axes: { ritam: [0, 0], rez: [0, 0], zvuk: [0, 0], boja: [0, 0], ideja: [0, 0] },
});

export function tallyFrom(rows: ReadonlyArray<{ side: number; axis: string; n: number }>): Tally {
  const tally = emptyTally();
  for (const row of rows) {
    if (!isOsa(row.axis)) continue;
    const side: Side = row.side === 1 ? 1 : 0;
    tally.votes[side] += row.n;
    tally.axes[row.axis][side] += row.n;
  }
  return tally;
}

export const isOsa = (value: unknown): value is Osa =>
  typeof value === 'string' && (OSE as readonly string[]).includes(value);

export const totalVotes = (tally: Tally): number => tally.votes[0] + tally.votes[1];

/* ------------------------------------------------------------------ where it is */

export type Stage =
  /** Waiting for the other side to answer. */
  | 'poziv'
  /** Terms came back changed; it is the first mover's turn again. */
  | 'izmena'
  /** Agreed. The clock is running. */
  | 'rad'
  /** Both works are in (or the deadline passed); the community is deciding. */
  | 'glasanje'
  /** The outcome is known. */
  | 'ishod'
  | 'odbijen'
  | 'otkazan'
  /** Nobody answered in time, or nobody submitted anything. */
  | 'istekao';

/** Stages a member should still be looking at. Everything else is over and leaves no trace. */
export const ZIVE_FAZE: readonly Stage[] = ['poziv', 'izmena', 'rad', 'glasanje', 'ishod'];

/**
 * Stages that stand in the way of the next duel between the same two people.
 *
 * **`ishod` is deliberately not in here.** It has no exit — nothing ever moves a duel out of it —
 * so treating a finished duel as an obstacle meant Marko and Ana got exactly one duel between them,
 * ever, and were told „već imate dvoboj u toku" about something they had both already revised.
 */
export const BLOKIRA_NOVI: readonly Stage[] = ['poziv', 'izmena', 'rad', 'glasanje'];

/**
 * Stages either side may still pull out of.
 *
 * Not `glasanje`, which would throw away votes other people have already cast, and **never**
 * `ishod`: cancelling a decided duel flips it to `otkazan`, and `reviseRefusal` then refuses the
 * *other* member's revision on a node whose ordinary form does not offer one. That is a griefing
 * tool pointed straight at the only number GDD §12 says is worth measuring.
 */
export const MOZE_ODUSTATI: readonly Stage[] = ['poziv', 'izmena', 'rad'];

export function stageOf(duel: Duel, now: number): Stage {
  if (duel.cancelledAt) return 'otkazan';
  if (duel.declinedAt) return 'odbijen';
  if (!duel.acceptedAt) {
    if (now >= duel.termsAt + POZIV_TTL_MS) return 'istekao';
    return duel.rounds === 0 ? 'poziv' : 'izmena';
  }
  const opens = voteOpensAt(duel);
  if (opens === null || now < opens) return 'rad';
  // The deadline came and went with nothing in it. There is no duel to hold a vote about.
  if (!duel.fromAt && !duel.toAt) return 'istekao';
  // One side did not turn up. There is nothing to compare, so there is nothing to vote on.
  if (!duel.fromAt || !duel.toAt) return 'ishod';
  const closes = voteClosesAt(duel);
  return closes !== null && now < closes ? 'glasanje' : 'ishod';
}

/** Whose move it is while the terms are still being agreed. Null once the clock is running. */
export function turnOf(duel: Duel, now?: number): string | null {
  if (duel.acceptedAt || duel.declinedAt || duel.cancelledAt) return null;
  // An expired invitation is nobody's move. Every caller checks the stage first today, which is
  // the only reason this ever agreed with `stageOf`.
  if (now !== undefined && now >= duel.termsAt + POZIV_TTL_MS) return null;
  return duel.counterBy === duel.fromId ? duel.toId : duel.counterBy === null ? duel.toId : duel.fromId;
}

export const sideOf = (duel: Duel, memberId: string): Side | null =>
  memberId === duel.fromId ? 0 : memberId === duel.toId ? 1 : null;

export const idOfSide = (duel: Duel, side: Side): string => (side === 0 ? duel.fromId : duel.toId);
export const nameOfSide = (duel: Duel, side: Side): string =>
  side === 0 ? duel.fromName : duel.toName;
export const urlOfSide = (duel: Duel, side: Side): string | null =>
  side === 0 ? duel.fromUrl : duel.toUrl;
export const submittedAt = (duel: Duel, side: Side): number | null =>
  side === 0 ? duel.fromAt : duel.toAt;
export const revisedAt = (duel: Duel, side: Side): number | null =>
  side === 0 ? duel.fromRevisedAt : duel.toRevisedAt;

/* ------------------------------------------------------------------ the clock */

export function workDueAt(duel: Duel): number | null {
  return duel.acceptedAt === null ? null : duel.acceptedAt + duel.workHours * HOUR;
}

/**
 * Voting opens the moment both works are in, not when the deadline happens to arrive. Two people
 * who both finished on Tuesday should not wait until Thursday to find out.
 */
export function voteOpensAt(duel: Duel): number | null {
  const due = workDueAt(duel);
  if (due === null) return null;
  if (duel.fromAt && duel.toAt) return Math.min(Math.max(duel.fromAt, duel.toAt), due);
  return due;
}

export function voteClosesAt(duel: Duel): number | null {
  const opens = voteOpensAt(duel);
  return opens === null ? null : opens + duel.voteHours * HOUR;
}

/** The deadline that is currently running, whichever phase it belongs to. Null when none is. */
export function deadlineNow(duel: Duel, now: number): number | null {
  switch (stageOf(duel, now)) {
    case 'poziv':
    case 'izmena':
      return duel.termsAt + POZIV_TTL_MS;
    case 'rad':
      return workDueAt(duel);
    case 'glasanje':
      return voteClosesAt(duel);
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ health */

const clamp = (n: number, low: number, high: number): number => Math.max(low, Math.min(high, n));

/**
 * The bar while the clock runs: how much of the agreed time is left. It stops the moment that side
 * submits, so watching the other bar keep draining after yours has stopped is the whole drama of a
 * deadline — and it has no bearing at all on who wins.
 */
export function clockHealth(duel: Duel, side: Side, now: number): number {
  const due = workDueAt(duel);
  if (duel.acceptedAt === null || due === null) return 100;
  const span = due - duel.acceptedAt;
  if (span <= 0) return 0;
  const at = submittedAt(duel, side) ?? now;
  return clamp(Math.round(100 * (1 - (at - duel.acceptedAt) / span)), 0, 100);
}

/**
 * The bar while the vote runs: the share of the vote, scaled so that a duel with three votes in it
 * still moves. Floored until the duel resolves, because nobody sits at zero for two days.
 */
export function voteHealth(tally: Tally, side: Side, resolved: boolean): number {
  const total = totalVotes(tally);
  const against = tally.votes[other(side)];
  const raw = Math.round(100 * (1 - against / Math.max(HP_SKALA, total)));
  return clamp(raw, resolved ? 0 : HP_POD, 100);
}

/** What the bar reads right now, whichever phase the duel is in. */
export function healthOf(duel: Duel, tally: Tally, side: Side, now: number): number {
  const stage = stageOf(duel, now);
  if (stage === 'poziv' || stage === 'izmena' || stage === 'rad') return clockHealth(duel, side, now);
  if (stage === 'glasanje') return voteHealth(tally, side, false);
  if (stage === 'ishod') {
    const ishod = ishodOf(duel, tally, now);
    if (ishod.kind === 'predaja') return ishod.winner === side ? 100 : 0;
    return voteHealth(tally, side, true);
  }
  return 100;
}

/**
 * True when this axis is the one already doing the most damage to that side — „veoma efikasno".
 * Not decoration: it is the screen telling somebody they keep losing on rhythm.
 */
export function superEffective(tally: Tally, against: Side, axis: Osa): boolean {
  const attacker = other(against);
  const here = tally.axes[axis][attacker];
  if (here < 2) return false;
  return OSE.every((o) => o === axis || tally.axes[o][attacker] <= here);
}

/* ------------------------------------------------------------------ the outcome */

export type Ishod =
  /** The community decided. */
  | { kind: 'pobeda'; winner: Side; decisive: Osa | null }
  /** One side did not turn up, so there was nothing to compare. */
  | { kind: 'predaja'; winner: Side }
  | { kind: 'nereseno'; reason: 'malo-glasova' | 'izjednaceno' }
  /** Neither of them submitted anything. */
  | { kind: 'nista' };

/**
 * Derived, never stored. The database keeps `resolved_at` so that the feedback stamp happens once,
 * but the answer itself is recomputed from the votes every time — a cached winner is a column that
 * can disagree with the votes it was computed from.
 */
export function ishodOf(duel: Duel, tally: Tally, now: number): Ishod {
  if (stageOf(duel, now) !== 'ishod') return { kind: 'nista' };
  if (!duel.fromAt && !duel.toAt) return { kind: 'nista' };
  if (!duel.toAt) return { kind: 'predaja', winner: 0 };
  if (!duel.fromAt) return { kind: 'predaja', winner: 1 };

  const total = totalVotes(tally);
  if (total < MIN_GLASOVA) return { kind: 'nereseno', reason: 'malo-glasova' };
  if (tally.votes[0] === tally.votes[1]) return { kind: 'nereseno', reason: 'izjednaceno' };
  const winner: Side = tally.votes[0] > tally.votes[1] ? 0 : 1;
  return { kind: 'pobeda', winner, decisive: decisiveAxis(tally, winner) };
}

/** The axis the winner won by the widest margin. Null when nothing stood out. */
export function decisiveAxis(tally: Tally, winner: Side): Osa | null {
  let best: Osa | null = null;
  let margin = 0;
  for (const axis of OSE) {
    const gap = tally.axes[axis][winner] - tally.axes[axis][other(winner)];
    if (gap > margin) {
      margin = gap;
      best = axis;
    }
  }
  return best;
}

/** Axes this side won, for the sentence that tells somebody what they did right. */
export function axesWonBy(tally: Tally, side: Side): Osa[] {
  return OSE.filter((axis) => tally.axes[axis][side] > tally.axes[axis][other(side)]);
}

/** True when the row still needs the write that stamps the outcome and unlocks both revisions. */
export function needsResolving(duel: Duel, now: number): boolean {
  return !duel.resolvedAt && stageOf(duel, now) === 'ishod';
}

/* ------------------------------------------------------------------ blind voting */

/**
 * Which side is shown as RAD A, per viewer. Stable for a given (key, viewer) so a reload never
 * relabels the works somebody is halfway through comparing, and different between viewers so that
 * „the first one" is not a tell.
 *
 * Deterministic rather than stored: a shuffle in the database would be a per-viewer row, and this
 * is one line of arithmetic.
 *
 * **Do not call this from the browser.** It lives here because it is pure and testable, and it is
 * wrapped by `src/server/blind.ts`, which salts it with a constant that never leaves the Worker.
 * Called with a key a member can guess, it hands them back the mapping the blindness exists to
 * withhold.
 */
export function blindOrder(key: string, viewerId: string): [Side, Side] {
  let h = 2166136261;
  const seed = `${key}:${viewerId}`;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  /*
   * FNV-1a, then an avalanche — and the second half is not optional here.
   *
   * Multiplying by an odd constant preserves the low bit, so FNV-1a's bit 0 is nothing but the XOR
   * parity of every character that went in. Taking the answer straight off it made this function
   * ignore any prefix whose own parity was even, which is to say it ignored the server's salt
   * completely. The mixer below is murmur3's finalizer; `tests/dvoboj.test.ts` measures that
   * salting actually moves the answer, because reading the code did not catch this and a
   * measurement does.
   */
  h ^= h >>> 15;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909);
  h ^= h >>> 16;
  return (h >>> 0) % 2 === 0 ? [0, 1] : [1, 0];
}

export type BlindLabel = 'A' | 'B';

export const isBlindLabel = (value: unknown): value is BlindLabel => value === 'A' || value === 'B';

/* ------------------------------------------------------------------ who may do what */

export type ChallengeRefusal =
  | 'sam-sebi'
  | 'van-posade'
  | 'nije-otkljucano'
  | 'vec-traje'
  | 'previse-poziva'
  | 'skoro-odbijen'
  | 'nema-zadatka'
  | 'istekao';

export interface ChallengeCheck {
  me: { id: string; visible: boolean; readOnly: boolean; unlocked: boolean };
  them: { id: string; visible: boolean; unlocked: boolean; hidden: boolean };
  /** Duels between these two that are not over. */
  betweenUs: readonly Duel[];
  /** Challenges this member has sent and not had answered. */
  myOpenChallenges: number;
  /** When they last declined this member, if they have. */
  lastDeclinedAt: number | null;
  /** How many briefs the two of them could actually agree on. */
  briefsAvailable: number;
  now: number;
}

/**
 * The one rule about who may challenge whom: **both of you are in the crew, and there is a brief
 * you could both work on.** Not „you are ahead of them", not „you have posted more" — those would
 * be a rank in disguise, which is what GDD §10.1 rules out.
 *
 * The rest of the list is not about worthiness, it is about not being able to wear somebody down.
 */
export function challengeRefusal(check: ChallengeCheck): ChallengeRefusal | null {
  const { me, them, now } = check;
  if (me.readOnly) return 'istekao';
  if (me.id === them.id) return 'sam-sebi';
  // „Sakrij" already means this person does not reach me. A challenge is reaching somebody.
  if (!me.visible || !them.visible || them.hidden) return 'van-posade';
  if (!me.unlocked || !them.unlocked) return 'nije-otkljucano';
  if (check.betweenUs.some((duel) => BLOKIRA_NOVI.includes(stageOf(duel, now)))) return 'vec-traje';
  if (check.myOpenChallenges >= MAX_OTVORENIH_POZIVA) return 'previse-poziva';
  if (check.lastDeclinedAt !== null && now - check.lastDeclinedAt < PAUZA_POSLE_ODBIJANJA_MS)
    return 'skoro-odbijen';
  if (check.briefsAvailable <= 0) return 'nema-zadatka';
  return null;
}

/** A brief, with what each side has already done with it. */
export interface Brief {
  node: ResolvedNode;
  /** This member has already posted a work on it. */
  redoMine: boolean;
  /** The other one has. */
  redoTheirs: boolean;
}

/**
 * The briefs two people could agree on: a node that asks for a link and that **both of them have
 * unlocked**. That is the whole rule.
 *
 * It used to also require that neither had posted it, which kept the accounting tidy — a `rad`
 * counted exactly once — and quietly refused the most obvious reason to want a duel at all:
 * redoing something you have already done, on purpose, against somebody. An exercise you finished
 * in March is a better brief than one you have never seen, because you already know what you got
 * wrong. So a done exercise is offered, and marked.
 *
 * What a redo does *not* do is count twice. `progress` holds one row per node and `applyProgress`
 * keeps the first `v1_url` it was given, so a second work on the same node moves neither `radovi`
 * nor `popravljeni`. That is the honest answer rather than a limitation: one step of the
 * curriculum, one `rad`, however many times you feel like doing it. It does still have to be a
 * *new* work — `applyProgress` refuses a link this member has posted before — so a redo cannot be
 * the old export handed in again.
 *
 * Fresh briefs sort first, because the default should still be a step that moves somebody forward.
 */
/**
 * How many free-island briefs a challenge may show.
 *
 * Every course island contributes at most a handful of postable nodes, and both members have to
 * have unlocked each one — so the list has always been short by construction. A free island breaks
 * that: it is unlocked for everybody, nothing on it is sequenced, and it holds the whole exercise
 * catalogue. Uncapped, a challenge screen would become forty rows in the one picker in this
 * product that has no filter, which is the overwhelm this whole thing exists to cure arriving
 * through a side door.
 */
export const FREE_BRIEF_LIMIT = 5;

export function briefsFor(
  mine: { content: ResolvedContent; state: PlayerState },
  theirs: { content: ResolvedContent; state: PlayerState },
): Brief[] {
  const out: Brief[] = [];
  const free: Brief[] = [];
  for (const island of mine.content.islands) {
    const theirIsland = theirs.content.islands.find((i) => i.id === island.id);
    if (!theirIsland) continue;
    for (const node of island.nodes) {
      if (!node.posts) continue;
      const theirNode = theirIsland.nodes.find((n) => n.id === node.id);
      if (!theirNode) continue;
      if (!isNodeUnlocked(mine.content, mine.state, island, node)) continue;
      if (!isNodeUnlocked(theirs.content, theirs.state, theirIsland, theirNode)) continue;
      const brief: Brief = {
        node,
        redoMine: Boolean(mine.state.progress[node.id]?.v1At),
        redoTheirs: Boolean(theirs.state.progress[node.id]?.v1At),
      };
      // A free island's briefs are capped, but a redo is never dropped: if either of you has
      // already done it, it is a brief you both understand, and that is the best kind to duel over.
      if (island.free && !brief.redoMine && !brief.redoTheirs) free.push(brief);
      else out.push(brief);
    }
  }
  // Authored order, which on a free island is the editorial recommendation.
  out.push(...free.slice(0, FREE_BRIEF_LIMIT));
  const rank = (brief: Brief): number => (brief.redoMine || brief.redoTheirs ? 1 : 0);
  return out.sort((a, b) => rank(a) - rank(b));
}

export type VoteRefusal = 'nije-vreme' | 'ti-si-u-njemu' | 'van-posade' | 'nije-otkljucano';

export function voteRefusal(check: {
  duel: Duel;
  voter: { id: string; visible: boolean; unlocked: boolean };
  now: number;
}): VoteRefusal | null {
  const { duel, voter, now } = check;
  if (sideOf(duel, voter.id) !== null) return 'ti-si-u-njemu';
  if (!voter.visible) return 'van-posade';
  if (!voter.unlocked) return 'nije-otkljucano';
  if (stageOf(duel, now) !== 'glasanje') return 'nije-vreme';
  return null;
}

export type SubmitRefusal = 'nije-tvoj' | 'nije-vreme' | 'vec-si';

export function submitRefusal(duel: Duel, memberId: string, now: number): SubmitRefusal | null {
  const side = sideOf(duel, memberId);
  if (side === null) return 'nije-tvoj';
  if (stageOf(duel, now) !== 'rad') return 'nije-vreme';
  if (submittedAt(duel, side) !== null) return 'vec-si';
  return null;
}

/**
 * The revision, which is the point of the whole feature.
 *
 * A duel always earns one, even on a node whose ordinary form does not ask for a revision. The
 * `needsRevision` guard in `applyProgress` exists to stop somebody claiming a revision that nobody
 * critiqued — and a resolved duel is proof of critique. That is why this lives here and the duel's
 * revision has a route of its own.
 */
export type ReviseRefusal = 'nije-tvoj' | 'nije-gotov' | 'nisi-predao' | 'vec-si';

export function reviseRefusal(duel: Duel, memberId: string, now: number): ReviseRefusal | null {
  const side = sideOf(duel, memberId);
  if (side === null) return 'nije-tvoj';
  if (stageOf(duel, now) !== 'ishod') return 'nije-gotov';
  if (submittedAt(duel, side) === null) return 'nisi-predao';
  if (revisedAt(duel, side) !== null) return 'vec-si';
  return null;
}

/* ------------------------------------------------------------------ terms */

export interface Terms {
  nodeId: string;
  workHours: number;
  voteHours: number;
}

export const validTerms = (terms: Terms): boolean =>
  typeof terms.nodeId === 'string' &&
  terms.nodeId.length > 0 &&
  terms.nodeId.length <= 120 &&
  (ROKOVI_RADA as readonly number[]).includes(terms.workHours) &&
  (ROKOVI_GLASANJA as readonly number[]).includes(terms.voteHours);

export const sameTerms = (duel: Duel, terms: Terms): boolean =>
  duel.nodeId === terms.nodeId &&
  duel.workHours === terms.workHours &&
  duel.voteHours === terms.voteHours;

/* ------------------------------------------------------------------ what needs you */

/**
 * The single duel worth handing the SADA card to, or null.
 *
 * Only ever a duel that is waiting on **this** member. Somebody else's turn is not a next step,
 * and SADA is the one screen in this product that is allowed to say what to do now (GDD §2①) — a
 * duel that is merely running belongs on a strip underneath, with the crew and the deferred
 * revision, which is what `waitingDuels` is for.
 */
export function urgentDuel(duels: readonly Duel[], memberId: string, now: number): Duel | null {
  let best: Duel | null = null;
  let bestKey = Number.POSITIVE_INFINITY;
  for (const duel of duels) {
    const rank = urgencyOf(duel, memberId, now);
    if (rank === null) continue;
    const deadline = deadlineNow(duel, now) ?? Number.MAX_SAFE_INTEGER;
    const key = rank * 1e15 + deadline;
    if (key < bestKey) {
      bestKey = key;
      best = duel;
    }
  }
  return best;
}

/**
 * Lower is more urgent. Null when this duel is not waiting on this member at all.
 *
 * The clock outranks the revision, which is the one place this product does *not* put the revision
 * first (GDD §2③) — and the reason is that they fail differently. A submission deadline that
 * passes takes the duel with it; a revision has no deadline, is never taken away, and becomes the
 * card by itself the moment the clock is gone. Ranking them the other way round would lose
 * somebody a duel to protect a step that was in no danger.
 */
function urgencyOf(duel: Duel, memberId: string, now: number): number | null {
  const side = sideOf(duel, memberId);
  if (side === null) return null;
  switch (stageOf(duel, now)) {
    case 'rad':
      // The only thing here with a clock that can take something away.
      return submittedAt(duel, side) === null ? 0 : null;
    case 'ishod':
      return revisedAt(duel, side) === null && submittedAt(duel, side) !== null ? 1 : null;
    case 'poziv':
    case 'izmena':
      /*
       * Last, and that is the point. Ranking somebody else's unanswered invitation first let any
       * member of the crew replace your next step — including a revision you already owed — on the
       * one screen this app opens on, without your consent. `docs/UZIVO.md` §1 refused a far
       * smaller version of that, and GDD §2① is the rule it was refusing on.
       */
      return turnOf(duel, now) === memberId ? 2 : null;
    default:
      return null;
  }
}

/**
 * Duels that are running but are not this member's move — waiting for an answer, waiting for the
 * other work, waiting for the community.
 *
 * They still need a way in: the drafted Skool post lives on the arena screen, and a duel nobody
 * can reach is a duel nobody brings voters to. So they get a strip under the card rather than the
 * card itself, the same shape the crew and the deferred revision already use.
 */
export function waitingDuels(duels: readonly Duel[], memberId: string, now: number): Duel[] {
  return duels
    .filter(
      (duel) =>
        sideOf(duel, memberId) !== null &&
        // Not `ishod`: it has no exit, so a finished duel would sit on the strip for good.
        BLOKIRA_NOVI.includes(stageOf(duel, now)) &&
        urgencyOf(duel, memberId, now) === null,
    )
    .sort((a, b) => (deadlineNow(a, now) ?? Infinity) - (deadlineNow(b, now) ?? Infinity));
}

/** Duels this member could vote on, oldest deadline first. */
export function votableDuels(duels: readonly Duel[], memberId: string, now: number): Duel[] {
  return duels
    .filter((duel) => sideOf(duel, memberId) === null && stageOf(duel, now) === 'glasanje')
    .sort((a, b) => (voteClosesAt(a) ?? 0) - (voteClosesAt(b) ?? 0));
}
